import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  FormControl,
  Divider,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import {
  BaseReportComponentProps,
  MdaReportTypeField,
  MdaReportTypes,
  PerformanceDataType,
  PerformanceMethodology,
  PeriodBaseRequest,
} from '../../store/types';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo } from 'react';
import { BenchmarkSelect } from 'src/features/securities/performance/components/benchmarkSelect';
import { SecurityBenchmark } from 'src/features/securities/performance/store/types';
import { DateRange } from 'src/store';
import { ClientPortfolio } from '../../../common/store';

import { ReportGroupingOption } from '../ReportGroupingOption';
import { MultiplePeriodSelector } from '../MultiplePeriodSelector';
import DateRangePicker from 'src/common/components/DateRangePicker';

export interface PortfolioSnapshotProps extends BaseReportComponentProps {
  setPortfolioSnapshotSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangePortfolioSnapshotParameter: ActionCreatorWithPayload<DateRange>;
  setPortfolioSnapshotPortfolioParameter?: ActionCreatorWithPayload<number, string>;
  setPortfolioSnapshotBenchmarkParameter: ActionCreatorWithPayload<number>;
  setPortfolioSnapshotPerformanceDataTypeParameter: ActionCreatorWithPayload<PerformanceDataType, string>;
  setPortfolioSnapshotPerformanceMethodology: ActionCreatorWithPayload<PerformanceMethodology, string>;
  expandPortfolioSnapshot: ActionCreatorWithoutPayload<string>;
  setPortfolioSnapshotApplyExclusionList: ActionCreatorWithPayload<boolean>;
  setPortfolioSnapshotIncludeAfterFeesReturn: ActionCreatorWithPayload<boolean>;
  setPortfolioSnapshotExcludeFrankingCredits: ActionCreatorWithPayload<boolean>;
  setPortfolioSnapshotPrimaryBenchmark: ActionCreatorWithPayload<boolean>;
  setPortfolioSnapshotSecondaryBenchmark: ActionCreatorWithPayload<boolean>;
  setPortfolioSnapshotIncludeDetailTable: ActionCreatorWithPayload<boolean>;
  clientPortfolios: ClientPortfolio[];
  benchmarks: SecurityBenchmark[];
  setPortfolioSnapshotMultiplePeriods: ActionCreatorWithPayload<PeriodBaseRequest[], string>;
}

export const PortfolioSnapshot = ({
  parameters,
  setPortfolioSnapshotSelectedParameter,
  setDateRangePortfolioSnapshotParameter,
  setPortfolioSnapshotPortfolioParameter,
  setPortfolioSnapshotBenchmarkParameter,
  setPortfolioSnapshotPerformanceDataTypeParameter,
  setPortfolioSnapshotPerformanceMethodology,
  setPortfolioSnapshotApplyExclusionList,
  setPortfolioSnapshotIncludeAfterFeesReturn,
  setPortfolioSnapshotExcludeFrankingCredits,
  setPortfolioSnapshotPrimaryBenchmark,
  setPortfolioSnapshotSecondaryBenchmark,
  setPortfolioSnapshotIncludeDetailTable,
  expandPortfolioSnapshot,
  benchmarks,
  clientPortfolios,
  setGroupConsolidatedOption,
  createReportSortRequest,
  setPortfolioSnapshotMultiplePeriods,
  disablePortfolioSelect,
}: PortfolioSnapshotProps) => {
  const handleClickPortfolioSnapshotSelect = () => {
    setPortfolioSnapshotSelectedParameter(!parameters.selectedReports?.portfolioSnapshot.selected);
    createReportSortRequest(
      !parameters.selectedReports?.portfolioSnapshot.selected,
      MdaReportTypes.PortfolioSnapshot.id,
      MdaReportTypes.PortfolioSnapshot.displayName
    );
  };

  const handleSelectPortfolio = (portfolio: ClientPortfolio | null) => {
    if (!setPortfolioSnapshotPortfolioParameter) return;
    setPortfolioSnapshotPortfolioParameter(portfolio?.investmentProgramId ?? -1);
  };

  const handleClickDateForPortfolioSnapshot = (dates: DateRange) => {
    setDateRangePortfolioSnapshotParameter(dates);
  };

  const setBenchmark = (benchmark: SecurityBenchmark | null) => {
    setPortfolioSnapshotBenchmarkParameter(benchmark?.id ?? -1);
  };

  const datesAreLessThan6Months = useMemo(() => {
    const { fromDate, toDate } = parameters.selectedReports.portfolioSnapshot;
    const from = DateTime.fromISO(fromDate);
    const to = DateTime.fromISO(toDate);
    const diffInMonths = to.diff(from, 'months').months;
    return diffInMonths < 6;
  }, [parameters.selectedReports.portfolioSnapshot.fromDate, parameters.selectedReports.portfolioSnapshot.toDate]);

  const datesAreOver3Years = useMemo(() => {
    const { fromDate, toDate } = parameters.selectedReports.portfolioSnapshot;
    const from = DateTime.fromISO(fromDate);
    const to = DateTime.fromISO(toDate);
    const diffInYears = to.diff(from, 'years').years;
    return diffInYears > 3;
  }, [parameters.selectedReports.portfolioSnapshot.fromDate, parameters.selectedReports.portfolioSnapshot.toDate]);

  useEffect(() => {
    if (datesAreLessThan6Months) {
      if (parameters.selectedReports.portfolioSnapshot.performanceMethodology === PerformanceMethodology.ReturnOnAverage) {
        setPortfolioSnapshotPerformanceDataTypeParameter(PerformanceDataType.Monthly);
      } else {
        setPortfolioSnapshotPerformanceDataTypeParameter(PerformanceDataType.Daily);
      }
    }
  }, [datesAreLessThan6Months]);

  useEffect(() => {
    const isMonthly =
      datesAreOver3Years || parameters.selectedReports.portfolioSnapshot.performanceMethodology === PerformanceMethodology.ReturnOnAverage;
    setPortfolioSnapshotPerformanceDataTypeParameter(isMonthly ? PerformanceDataType.Monthly : PerformanceDataType.Daily);
  }, [datesAreOver3Years]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioSnapshot.performanceMethodology === PerformanceMethodology.ReturnOnAverage) {
      setPortfolioSnapshotPerformanceDataTypeParameter(PerformanceDataType.Monthly);
    } else {
      if (datesAreLessThan6Months) {
        setPortfolioSnapshotPerformanceDataTypeParameter(PerformanceDataType.Daily);
      }
    }
  }, [parameters.selectedReports.portfolioSnapshot.performanceMethodology]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioSnapshot.benchmarkId !== -1) {
      setPortfolioSnapshotPrimaryBenchmark(false);
      setPortfolioSnapshotSecondaryBenchmark(false);
    }
  }, [parameters.selectedReports.portfolioSnapshot.benchmarkId]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioSnapshot.primaryBenchmark) {
      setPortfolioSnapshotSecondaryBenchmark(false);
      setBenchmark(null);
    }
  }, [parameters.selectedReports.portfolioSnapshot.primaryBenchmark]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioSnapshot.secondaryBenchmark) {
      setPortfolioSnapshotPrimaryBenchmark(false);
      setBenchmark(null);
    }
  }, [parameters.selectedReports.portfolioSnapshot.secondaryBenchmark]);

  return (
    <Accordion onChange={expandPortfolioSnapshot} expanded={parameters.selectedReports?.portfolioSnapshot.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.portfolioSnapshot.selected}
              onChangeHandler={handleClickPortfolioSnapshotSelect}
            />
          }
          label="Portfolio Snapshot"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: parameters?.selectedReports.portfolioSnapshot?.fromDate,
              dateTo: parameters?.selectedReports.portfolioSnapshot?.toDate,
              dateRangeType: parameters?.selectedReports.portfolioSnapshot?.dateRangeType,
            }}
            setDateRange={handleClickDateForPortfolioSnapshot}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1} direction="column">
          <Grid item>
            <MultiplePeriodSelector
              maxPeriods={3}
              disableInceptionRemoval={false}
              onChange={(periods: PeriodBaseRequest[]) => {
                setPortfolioSnapshotMultiplePeriods(periods);
              }}
              values={parameters.selectedReports.portfolioSnapshot.multiPeriods || []}
            />
          </Grid>
          {setPortfolioSnapshotPortfolioParameter && !disablePortfolioSelect && (
            <Grid item>
              <Autocomplete
                noOptionsText="No items to select"
                id="portfoliosnapshot-multiselect"
                fullWidth
                limitTags={1}
                value={
                  clientPortfolios.find((portfolio) => portfolio.investmentProgramId === parameters.selectedReports.portfolioSnapshot.portfolioId) ||
                  null
                }
                options={clientPortfolios}
                onChange={(_e: React.SyntheticEvent<Element, Event>, value: ClientPortfolio | null) => handleSelectPortfolio(value)}
                getOptionLabel={(option: ClientPortfolio) => option.investmentServiceName}
                renderInput={(params) => <TextField {...params} label="Select a Portfolio" margin="normal" fullWidth />}
                isOptionEqualToValue={(option: ClientPortfolio, value: ClientPortfolio) => option.investmentProgramId === value.investmentProgramId}
              />
            </Grid>
          )}
          <Grid item container spacing={1}>
            <Grid item>
              <BenchmarkSelect
                setBenchmark={setBenchmark}
                selectedItem={benchmarks.find((benchmark) => benchmark.id === parameters.selectedReports.portfolioSnapshot.benchmarkId) ?? null}
                items={benchmarks}
                disabled={
                  parameters.selectedReports.portfolioSnapshot.primaryBenchmark || parameters.selectedReports.portfolioSnapshot.secondaryBenchmark
                }
              />
            </Grid>
            <Grid item flex={1}>
              <FormControl fullWidth>
                <InputLabel id="performance-data-type-id">Performance Data</InputLabel>
                <WO2Select
                  fullWidth
                  labelId="performance-data-type-id"
                  label="Performance Data"
                  onChange={(e) => setPortfolioSnapshotPerformanceDataTypeParameter(e.target.value as PerformanceDataType)}
                  value={parameters.selectedReports.portfolioSnapshot.performanceDataType}
                >
                  <MenuItem
                    value={PerformanceDataType.Daily}
                    disabled={
                      datesAreOver3Years ||
                      parameters.selectedReports.portfolioSnapshot.performanceMethodology === PerformanceMethodology.ReturnOnAverage
                    }
                  >
                    Daily
                  </MenuItem>
                  <MenuItem disabled={datesAreLessThan6Months} value={PerformanceDataType.Monthly}>
                    Monthly
                  </MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid item container spacing={2}>
            <Grid item container xs={3} alignItems="center">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioSnapshot.applyExclusionList}
                onChange={(e) => setPortfolioSnapshotApplyExclusionList(e.target.checked)}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Apply Exclusion List</Typography>
            </Grid>
            <Grid item container alignItems="center" xs={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioSnapshot.includeAfterFeesReturn}
                onChange={(e) => setPortfolioSnapshotIncludeAfterFeesReturn(e.target.checked)}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Include After Fees Return</Typography>
            </Grid>
            <Grid item container alignItems="center" xs={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioSnapshot.excludeFrankingCredits}
                onChange={(e) => setPortfolioSnapshotExcludeFrankingCredits(e.target.checked)}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Exclude Franking Credits</Typography>
            </Grid>
            <Grid item container alignItems="center" xs={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioSnapshot.includeDetailTable}
                onChange={(e) => setPortfolioSnapshotIncludeDetailTable(e.target.checked)}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Include Detail Table</Typography>
            </Grid>
            <Grid item container alignItems="center" xs={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioSnapshot.primaryBenchmark}
                onChange={(e) => setPortfolioSnapshotPrimaryBenchmark(e.target.checked)}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={
                  parameters.selectedReports.portfolioSnapshot.secondaryBenchmark || parameters.selectedReports.portfolioSnapshot.benchmarkId !== -1
                }
              />
              <Typography variant="h4">Use Primary Benchmark</Typography>
            </Grid>
            <Grid item container alignItems="center" xs={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioSnapshot.secondaryBenchmark}
                onChange={(e) => setPortfolioSnapshotSecondaryBenchmark(e.target.checked)}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={
                  parameters.selectedReports.portfolioSnapshot.primaryBenchmark || parameters.selectedReports.portfolioSnapshot.benchmarkId !== -1
                }
              />
              <Typography variant="h4">Use Secondary Benchmark</Typography>
            </Grid>
          </Grid>

          <Grid item>
            <FormControl fullWidth>
              <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
              <WO2Select
                labelId="performance-methodology"
                label="performance methodology"
                onChange={(e) => setPortfolioSnapshotPerformanceMethodology(e.target.value as PerformanceMethodology)}
                value={parameters.selectedReports.portfolioSnapshot.performanceMethodology}
                defaultValue={PerformanceMethodology.TimeWeighted}
              >
                <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
                <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>
          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column" mt={1}>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={parameters.selectedReports.portfolioSnapshot.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.PortfolioSnapshot}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
